// src/pages/DocketGenie/components/CaseDetailsModal.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  X,
  FileText, // For primary info
  Scale, // For status/sentence
  Users, // For case parties
  List, // For counts
  Clock, // For duration
  Gavel, // For judges
  User, // For defendants
  Briefcase, // For attorneys
  Search, // For find similar
  RefreshCw, // For refresh case
  Printer, // For print
  Share2 // For export
} from 'lucide-react';
import { CountPropType, PersonPropType } from '../../../types/propTypes';
import { MessageSquare } from 'lucide-react';
import CommentsSection from './CommentsSection';
import { getStatusColorNoBorder } from '../../../utils/caseStatusUtils';
import { API_URLS } from '../../../constants/api';
import { openLACourtWithCaseNumber } from '../../../utils/laCourtAccess';
import { formatDateForDisplay, formatDateOnly } from '../../../utils/dateUtils';
import { RACES } from '../../../constants/races';
import { GENDERS } from '../../../constants/genders';
import { Edit2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

// Helper function to get sentencing summary
const getSentencingSummary = (caseData) => {
  const parts = [];

  // Check if all charges are dismissed
  if (caseData.isDismissed) {
    return 'Dismissed';
  }

  // Add confinement if present
  if (caseData.totalConfinementMonths && caseData.totalConfinementMonths > 0) {
    const years = caseData.totalConfinementMonths / 12;
    const months = caseData.totalConfinementMonths % 12;
    let confinementText = '';
    if (years >= 1) {
      confinementText = `${Math.floor(years)} year${Math.floor(years) > 1 ? 's' : ''}`;
      if (months > 0)
        confinementText += ` ${months} month${months > 1 ? 's' : ''}`;
    } else {
      confinementText = `${months} month${months > 1 ? 's' : ''}`;
    }
    parts.push(confinementText);
  }

  // Add confinement days if present
  if (caseData.totalConfinementDays && caseData.totalConfinementDays > 0) {
    parts.push(
      `${caseData.totalConfinementDays} day${caseData.totalConfinementDays > 1 ? 's' : ''}`
    );
  }

  // Add fines if present
  if (caseData.totalFines && caseData.totalFines > 0) {
    parts.push(`$${caseData.totalFines.toFixed(2)} fine`);
  }

  // Add probation if present
  if (caseData.totalProbationMonths && caseData.totalProbationMonths > 0) {
    const years = caseData.totalProbationMonths / 12;
    const months = caseData.totalProbationMonths % 12;
    let probationText = '';
    if (years >= 1) {
      probationText = `${Math.floor(years)} year${Math.floor(years) > 1 ? 's' : ''}`;
      if (months > 0)
        probationText += ` ${months} month${months > 1 ? 's' : ''}`;
    } else {
      probationText = `${months} month${months > 1 ? 's' : ''}`;
    }
    parts.push(`${probationText} probation`);
  }

  // Add diversion if present
  if (caseData.hasDiversion) {
    parts.push('Diversion');
  }

  if (parts.length === 0) {
    return 'No sentence';
  }

  return parts.join(', ');
};

// Helper function to get sentencing display for counts
const getSentencingDisplay = (count) => {
  if (!count.sentencing) {
    // Fallback to legacy sentence
    return count.sentence
      ? `${count.sentence.value} ${count.sentence.unit}`
      : 'N/A';
  }

  const parts = [];

  // Add confinements
  if (
    count.sentencing.confinements &&
    count.sentencing.confinements.length > 0
  ) {
    count.sentencing.confinements.forEach((confinement) => {
      if (confinement.fine) {
        // This is a fine
        const fineText = confinement.displayText || confinement.fine.toString();
        const facilityText = confinement.facilityType
          ? ` (${confinement.facilityType})`
          : '';
        const consecutiveText = confinement.consecutive ? ' consecutive' : '';
        parts.push(`${fineText}${facilityText}${consecutiveText}`);
      } else if (confinement.dismissal) {
        // This is a dismissal
        const dismissalText =
          confinement.displayText ||
          confinement.dismissal.reason ||
          'Dismissed';
        const facilityText = confinement.facilityType
          ? ` (${confinement.facilityType})`
          : '';
        const consecutiveText = confinement.consecutive ? ' consecutive' : '';
        parts.push(`${dismissalText}${facilityText}${consecutiveText}`);
      } else if (confinement.fugitiveExtradition) {
        // This is a fugitive extradition
        const extraditionText =
          confinement.displayText || confinement.fugitiveExtradition.toString();
        const facilityText = confinement.facilityType
          ? ` (${confinement.facilityType})`
          : '';
        const consecutiveText = confinement.consecutive ? ' consecutive' : '';
        parts.push(`${extraditionText}${facilityText}${consecutiveText}`);
      } else if (confinement.sentence) {
        // This is a time-based sentence
        const sentenceText =
          confinement.displayText ||
          `${confinement.sentence.value} ${confinement.sentence.unit}`;
        const facilityText = confinement.facilityType
          ? ` (${confinement.facilityType})`
          : '';
        const consecutiveText = confinement.consecutive ? ' consecutive' : '';
        parts.push(`${sentenceText}${facilityText}${consecutiveText}`);
      } else {
        // Fallback to type
        parts.push(confinement.type);
      }
    });
  }

  // Add probations
  if (count.sentencing.probations && count.sentencing.probations.length > 0) {
    count.sentencing.probations.forEach((probation) => {
      if (probation.sentence) {
        // Has a specific term
        const sentenceText = `${probation.sentence.value} ${probation.sentence.unit}`;
        const typeText = probation.isDiversion ? ' diversion' : ' probation';
        const statusText = probation.status ? ` (${probation.status})` : '';
        parts.push(`${sentenceText}${typeText}${statusText}`);
      } else {
        // No specific term specified
        const typeText = probation.isDiversion ? 'Diversion' : 'Probation';
        const statusText = probation.status ? ` (${probation.status})` : '';
        parts.push(`${typeText}${statusText}`);
      }
    });
  }

  if (parts.length === 0) {
    return 'No sentence';
  }

  return parts.join(', ');
};

const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-3 mb-4 print:gap-2 print:mb-2">
    <Icon className="w-5 h-5 text-gray-600 print:hidden" strokeWidth={1.5} />
    <h3 className="text-lg font-medium text-gray-900 print:text-sm print:font-bold">
      {title}
    </h3>
  </div>
);

// Add this after SectionHeader component definition
SectionHeader.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired
};

const CaseDetailsModal = ({
  caseData = {
    caseNumber: '',
    jurisdiction: '',
    courthouse: '',
    caseStatus: '',
    counts: [],
    judges: [],
    defendants: [],
    defenseAttorneys: [],
    prosecutingAttorneys: [],
    charges: [],
    comments: []
  },
  onClose,
  onFindSimilar,
  onCaseUpdated
}) => {
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [isCopyingCaseNumber, setIsCopyingCaseNumber] = useState(false);
  const [isEditingDefendants, setIsEditingDefendants] = useState(false);
  const [editedDefendants, setEditedDefendants] = useState([]);
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const printRef = React.useRef();

  // Get current user for tracking edits
  const { currentUser } = useAuth();

  // Initialize edited defendants when entering edit mode
  React.useEffect(() => {
    if (isEditingDefendants && caseData.defendants) {
      setEditedDefendants(
        caseData.defendants.map((d) => ({
          ...d,
          dateOfBirth: d.dateOfBirth || null,
          race: d.race || null,
          gender: d.gender || null
        }))
      );
    }
  }, [isEditingDefendants, caseData.defendants]);

  // Save edits function
  const handleSaveEdits = async () => {
    if (!caseData.caseNumber) return;

    setIsSavingEdits(true);
    try {
      // Prepare edit data
      const editData = {
        defendants: editedDefendants.map((defendant, index) => ({
          index,
          fields: {
            dateOfBirth: defendant.dateOfBirth,
            race: defendant.race,
            gender: defendant.gender
          }
        }))
      };

      // Prepare headers with user ID if authenticated (use Firebase UID, not email)
      const headers = {
        'Content-Type': 'application/json'
      };

      if (currentUser?.uid) {
        headers['X-User-Id'] = currentUser.uid;
      }

      const response = await fetch(API_URLS.CASE_EDITS(caseData.caseNumber), {
        method: 'POST',
        headers,
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        setIsEditingDefendants(false);
        // Refresh case data - pass case number to the handler
        if (onCaseUpdated && caseData.caseNumber) {
          onCaseUpdated(caseData.caseNumber);
        }
      } else {
        const error = await response.json();
        alert(`Failed to save edits: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving edits:', error);
      alert('Failed to save edits. Please try again.');
    } finally {
      setIsSavingEdits(false);
    }
  };

  // Cancel editing
  const handleCancelEditing = () => {
    setIsEditingDefendants(false);
    setEditedDefendants([]);
  };

  // Prevent background scrolling when modal is open
  React.useEffect(() => {
    // Add modal-open class for CSS-based prevention
    document.body.classList.add('modal-open');

    // Prevent scrolling by using event prevention
    // This approach maintains the scroll position without visual jumping
    const preventScroll = (e) => {
      // Only prevent if the touch/scroll is not on the modal content
      const modal = document.querySelector('[data-modal-content]');
      if (modal && !modal.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Add event listeners to prevent scrolling
    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });
    document.addEventListener('keydown', (e) => {
      // Prevent arrow keys, page up/down, home, end from scrolling
      if ([32, 33, 34, 35, 36, 37, 38, 39, 40].includes(e.keyCode)) {
        const modal = document.querySelector('[data-modal-content]');
        if (modal && !modal.contains(e.target)) {
          e.preventDefault();
        }
      }
    });

    return () => {
      // Remove modal-open class
      document.body.classList.remove('modal-open');

      // Remove event listeners
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
      document.removeEventListener('keydown', preventScroll);
    };
  }, []);

  const handleRefreshCase = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setRefreshStatus('Starting update...');
    setRefreshProgress(0);

    const startTime = Date.now();

    // Start progress animation immediately
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / 12000) * 100, 95); // Max 95% until completion
      setRefreshProgress(progress);
    }, 100);

    try {
      // Start the refresh job
      const response = await fetch(
        `${API_URLS.SCRAPING}/refresh/${encodeURIComponent(caseData.caseNumber)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        // Handle rate limiting specifically
        if (response.status === 429) {
          const errorData = await response.json();
          const retryAfter = errorData.retryAfterSeconds || 10;
          const remainingTokens = errorData.remainingTokens || 0;

          clearInterval(progressInterval);
          setRefreshStatus(
            `Rate limit exceeded. Please wait ${retryAfter} seconds before trying again. (${remainingTokens} requests remaining)`
          );
          setRefreshProgress(0);
          setIsRefreshing(false);
          setIsRateLimited(true);

          // Auto-clear the message and reset rate limit state after the retry time
          setTimeout(
            () => {
              setRefreshStatus(null);
              setIsRateLimited(false);
            },
            (retryAfter + 2) * 1000
          );
          return;
        }

        throw new Error(`Failed to start update: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Direct response - case was updated successfully
        clearInterval(progressInterval);
        setRefreshStatus('Case data updated successfully!');
        setRefreshProgress(100);
        setIsRefreshing(false);

        // Refresh the case data from the backend
        if (onCaseUpdated) {
          onCaseUpdated(caseData.caseNumber);
        }

        // Show success message for a few seconds
        setTimeout(() => {
          setRefreshStatus(null);
          setRefreshProgress(0);
        }, 3000);
      } else {
        // Error response
        clearInterval(progressInterval);
        setRefreshStatus(`Update failed: ${result.error || 'Unknown error'}`);
        setRefreshProgress(0);
        setIsRefreshing(false);

        // Show error message for a few seconds
        setTimeout(() => {
          setRefreshStatus(null);
        }, 5000);
      }

      // No timeout needed since we get direct response
    } catch (error) {
      console.error('Error starting refresh:', error);
      clearInterval(progressInterval);
      setRefreshStatus(`Error: ${error.message}`);
      setIsRefreshing(false);
    }
  };

  const handleModalScroll = (e) => {
    // Prevent scroll events from bubbling to the background
    e.stopPropagation();
  };

  const handleBackdropClick = (e) => {
    // Close modal when clicking on the backdrop (outer div)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalClick = (e) => {
    // Prevent clicks on the modal content from closing the modal
    e.stopPropagation();
  };

  const generateCaseSummary = (caseData) => {
    const getPrimaryJudge = (judges) => {
      if (!judges || judges.length === 0) return 'No judge assigned';

      const realJudges = judges.filter(
        (judge) => judge.firstName && judge.firstName !== 'Department'
      );

      if (realJudges.length === 0) return 'No judge assigned';

      const judge = realJudges[0];
      return `${judge.firstName} ${judge.lastName}`;
    };

    const getTotalSentence = (counts) => {
      if (!counts || counts.length === 0) return 'No sentence';

      const totalMonths = counts.reduce((total, count) => {
        if (!count.sentence) return total;
        const { value, unit } = count.sentence;
        if (unit === 'months') return total + value;
        if (unit === 'years') return total + value * 12;
        if (unit === 'days') return total + value / 30.44;
        return total;
      }, 0);

      if (totalMonths === 0) return 'No sentence';
      if (totalMonths < 12) return `${totalMonths.toFixed(1)} months`;
      return `${(totalMonths / 12).toFixed(1)} years`;
    };

    const formatDate = (dateString) => {
      return formatDateForDisplay(dateString);
    };

    const judge = getPrimaryJudge(caseData.judges);
    const defendants =
      caseData.defendants
        ?.map((d) => `${d.firstName} ${d.lastName}`)
        .join(', ') || 'N/A';
    const charges =
      caseData.counts?.map((c) => c.chargeCode).join(', ') || 'No charges';
    const sentence = getTotalSentence(caseData.counts);

    return `Case: ${caseData.caseNumber}
Jurisdiction: ${caseData.jurisdiction}
Courthouse: ${caseData.courthouse}
Filed: ${formatDate(caseData.openDate)}
Closed: ${formatDate(caseData.closeDate)}
Duration: ${caseData.durationDays ? `${caseData.durationDays} days` : 'N/A'}
Judge: ${judge}
Defendants: ${defendants}
Charges: ${charges}
Sentence: ${sentence}
Status: ${caseData.caseStatus || 'N/A'}

Data from Briefed`;
  };

  const handleExport = async () => {
    const caseSummary = generateCaseSummary(caseData);
    const shareData = {
      title: 'Briefed Case Information',
      text: caseSummary
    };

    try {
      // Try native Web Share API first
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare(shareData)
      ) {
        await navigator.share(shareData);
        return;
      }
    } catch (error) {
      // Check if user cancelled the share (AbortError)
      if (error.name === 'AbortError') {
        // User cancelled, don't show error or fallback
        return;
      }
      console.log('Native sharing failed, falling back to clipboard');
    }

    // Fallback to clipboard copy
    try {
      await navigator.clipboard.writeText(caseSummary);
      // You might want to add a toast notification here
      console.log('Case information copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);

      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = caseSummary;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        console.log('Case information copied to clipboard (fallback)');
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        // Only show error if it's not a user cancellation
        if (fallbackError.name !== 'AbortError') {
          alert('Failed to copy case information. Please try again.');
        }
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  const handlePrint = () => {
    // Create a clean print document
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      // Fallback if popup is blocked - use the simple approach
      const printContainer = document.createElement('div');
      printContainer.innerHTML = printRef.current.innerHTML;
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '-9999px';
      printContainer.style.width = '8.5in';
      printContainer.style.fontSize = '12px';
      printContainer.style.lineHeight = '1.4';
      printContainer.style.color = '#000';
      printContainer.style.backgroundColor = '#fff';

      document.body.appendChild(printContainer);
      window.print();
      document.body.removeChild(printContainer);
      return;
    }

    // Create clean HTML for printing
    /* eslint-disable react/prop-types */
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Case Details - ${caseData.caseNumber}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              background: #fff;
              padding: 20px;
            }
            .print-header {
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
              margin-bottom: 20px;
            }
            .print-header h1 {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .print-header .case-info {
              font-size: 14px;
              font-weight: bold;
            }
            .print-header .generated {
              font-size: 10px;
              color: #666;
            }
            .section {
              margin-bottom: 20px;
              page-break-inside: avoid;
            }
            .section h3 {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 10px;
              color: #000;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px;
              margin-bottom: 15px;
            }
            .info-item {
              border: 1px solid #ccc;
              padding: 10px;
              background: #fff;
            }
            .info-item h4 {
              font-size: 10px;
              font-weight: bold;
              margin-bottom: 5px;
              color: #666;
              text-transform: uppercase;
            }
            .info-item p {
              font-size: 12px;
              margin: 0;
            }
            .table {
              width: 100%;
              border-collapse: collapse;
              font-size: 11px;
              margin-top: 10px;
            }
            .table th, .table td {
              border: 1px solid #ccc;
              padding: 6px 8px;
              text-align: left;
            }
            .table th {
              background: #f5f5f5;
              font-weight: bold;
              font-size: 10px;
            }
            .badge {
              display: inline-block;
              padding: 2px 6px;
              border: 1px solid #ccc;
              background: #f0f0f0;
              font-size: 10px;
              margin: 1px;
            }
            .print-footer {
              border-top: 1px solid #ccc;
              padding-top: 10px;
              margin-top: 30px;
              text-align: center;
              font-size: 10px;
              color: #666;
            }
            @media print {
              body { padding: 15px; }
              .section { margin-bottom: 15px; }
              .info-grid { gap: 10px; }
              .info-item { padding: 8px; }
            }
            @page {
              margin: 0.5in;
              size: letter;
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>Briefed Case Report</h1>
            <div class="case-info">Case #${caseData.caseNumber}</div>
            <div class="generated">Generated: ${new Date().toLocaleDateString()}</div>
          </div>
          
          <div class="section">
            <h3>Primary Information</h3>
            <div class="info-grid">
              <div class="info-item">
                <h4>Case Number</h4>
                <p>${caseData.caseNumber}</p>
              </div>
              <div class="info-item">
                <h4>Jurisdiction</h4>
                <p>${caseData.jurisdiction || 'N/A'}</p>
              </div>
              <div class="info-item">
                <h4>Courthouse</h4>
                <p>${caseData.courthouse || 'N/A'}</p>
              </div>
              <div class="info-item">
                <h4>Departments & Judges</h4>
                <p>${
                  caseData.departments && caseData.departments.length > 0
                    ? 'Dept ' + caseData.departments.join(', ')
                    : 'No departments assigned'
                }</p>
                <p>${
                  caseData.judges &&
                  caseData.judges.filter(
                    (j) => j.firstName && j.firstName !== 'Department'
                  ).length > 0
                    ? caseData.judges
                        .filter(
                          (j) => j.firstName && j.firstName !== 'Department'
                        )
                        .map((j) => j.firstName + ' ' + j.lastName)
                        .join(', ')
                    : 'No judges assigned'
                }</p>
              </div>
              <div class="info-item">
                <h4>Case Timeline</h4>
                <p>${formatDateForDisplay(caseData.openDate)}${caseData.closeDate ? ' → ' + formatDateForDisplay(caseData.closeDate) : ''}</p>
              </div>
              <div class="info-item">
                <h4>Duration</h4>
                <p>${caseData.durationDays ? caseData.durationDays + ' days' : 'N/A'}</p>
              </div>
            </div>
          </div>

          <div class="section">
            <h3>Status & Sentence</h3>
            <div class="info-grid">
              <div class="info-item">
                <h4>Status</h4>
                <p>${caseData.caseStatus || 'Unknown'}</p>
              </div>
              <div class="info-item">
                <h4>Sentencing Summary</h4>
                <p>${getSentencingSummary(caseData)}</p>
              </div>
            </div>
          </div>

          <div class="section">
            <h3>Case Parties</h3>
            <div class="info-grid">
              <div class="info-item">
                <h4>Defendants</h4>
                <p>${
                  caseData.defendants && caseData.defendants.length > 0
                    ? caseData.defendants
                        .map((d) => {
                          const name = d.firstName + ' ' + d.lastName;
                          const dob = d.dateOfBirth
                            ? `Date of Birth: ${formatDateOnly(d.dateOfBirth) || 'N/A'}`
                            : 'Date of Birth: N/A';
                          const race = d.race ? `Race: ${d.race}` : 'Race: N/A';
                          const gender = d.gender ? `Gender: ${d.gender}` : '';
                          const demographics = gender
                            ? `${race}, ${gender}`
                            : race;
                          return name + ' (' + dob + ', ' + demographics + ')';
                        })
                        .join('<br>')
                    : 'N/A'
                }</p>
              </div>
              <div class="info-item">
                <h4>Prosecuting Attorneys</h4>
                <p>${
                  caseData.prosecutingAttorneys &&
                  caseData.prosecutingAttorneys.length > 0
                    ? caseData.prosecutingAttorneys
                        .map((a) => a.firstName + ' ' + a.lastName)
                        .join(', ')
                    : 'N/A'
                }</p>
              </div>
              <div class="info-item">
                <h4>Defense Attorneys</h4>
                <p>${
                  caseData.defenseAttorneys &&
                  caseData.defenseAttorneys.length > 0
                    ? caseData.defenseAttorneys
                        .map((a) => a.firstName + ' ' + a.lastName)
                        .join(', ')
                    : 'N/A'
                }</p>
              </div>
            </div>
          </div>

          ${
            caseData.counts && caseData.counts.length > 0
              ? `
          <div class="section">
            <h3>Counts & Sentencing</h3>
            <table class="table">
              <thead>
                <tr>
                  <th>Count</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Sentencing</th>
                </tr>
              </thead>
              <tbody>
                ${caseData.counts
                  .map(
                    (count) => `
                  <tr>
                    <td>${count.countNumber}</td>
                    <td>${count.chargeCode}</td>
                    <td>${count.chargeDescription}</td>
                    <td>${getSentencingDisplay(count)}</td>
                  </tr>
                `
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
          `
              : ''
          }

          <div class="print-footer">
            End of Report | Briefed | Generated ${new Date().toLocaleDateString()}
          </div>
        </body>
      </html>
    `;
    /* eslint-enable react/prop-types */

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load, then print
    printWindow.onload = () => {
      printWindow.print();
      // Close the window after printing
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    };
  };

  // Handle touch events to prevent background scrolling on mobile
  const handleTouchStart = (e) => {
    // Prevent touch events from bubbling to the background
    e.stopPropagation();
  };

  const handleTouchMove = (e) => {
    // Prevent touch move events from bubbling to the background
    e.stopPropagation();

    // If this is a scroll gesture on the modal content, allow it
    // Otherwise prevent default to stop background scrolling
    const modalContent = e.currentTarget;
    const isScrollingModal =
      modalContent.scrollHeight > modalContent.clientHeight;

    if (!isScrollingModal) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e) => {
    // Prevent touch end events from bubbling to the background
    e.stopPropagation();
  };

  // Handle backdrop touch events
  const handleBackdropTouchStart = (e) => {
    // Only close if touching the backdrop itself, not child elements
    if (e.target === e.currentTarget) {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 modal-backdrop"
      onClick={handleBackdropClick}
      onTouchStart={handleBackdropTouchStart}
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col modal-content"
        data-modal-content
        onClick={handleModalClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex-shrink-0 bg-white border-b px-4 sm:px-6 py-4 flex justify-between items-center relative z-10">
          <h2 className="text-xl font-medium">Case Details</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
              title="Export case information"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <button
              onClick={handlePrint}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
              title="Print case details"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={handleRefreshCase}
              disabled={isRefreshing || isRateLimited}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                isRefreshing
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : isRateLimited
                    ? 'text-red-400 bg-red-50 cursor-not-allowed'
                    : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
              }`}
              title={
                isRateLimited
                  ? 'Rate limit exceeded. Please wait before trying again.'
                  : 'Update case data with latest information'
              }
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
              />
              <span className="hidden sm:inline">
                {isRefreshing ? 'Updating...' : 'Update Case'}
              </span>
            </button>
            {onFindSimilar && (
              <button
                onClick={() => {
                  onFindSimilar(caseData);
                  onClose();
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors"
                title="Find similar closed cases"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">
                  Find Similar Closed Cases
                </span>
                <span className="sm:hidden">Similar</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Refresh Status Bar */}
        {refreshStatus && (
          <div
            className={`flex-shrink-0 border-b px-4 sm:px-6 py-3 relative z-10 ${
              refreshStatus.includes('Rate limit exceeded')
                ? 'bg-red-50 border-red-200'
                : refreshStatus.includes('failed') ||
                    refreshStatus.includes('Error')
                  ? 'bg-red-50 border-red-200'
                  : refreshStatus.includes('successfully')
                    ? 'bg-green-50 border-green-200'
                    : 'bg-blue-50 border-blue-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <RefreshCw
                className={`w-4 h-4 ${
                  refreshStatus.includes('Rate limit exceeded')
                    ? 'text-red-600'
                    : refreshStatus.includes('failed') ||
                        refreshStatus.includes('Error')
                      ? 'text-red-600'
                      : refreshStatus.includes('successfully')
                        ? 'text-green-600'
                        : 'text-blue-600'
                } ${isRefreshing ? 'animate-spin' : ''}`}
              />
              <div className="flex-1">
                <p
                  className={`text-sm ${
                    refreshStatus.includes('Rate limit exceeded')
                      ? 'text-red-800'
                      : refreshStatus.includes('failed') ||
                          refreshStatus.includes('Error')
                        ? 'text-red-800'
                        : refreshStatus.includes('successfully')
                          ? 'text-green-800'
                          : 'text-blue-800'
                  }`}
                >
                  {refreshStatus}
                </p>
                {isRefreshing &&
                  !refreshStatus.includes('Rate limit exceeded') && (
                    <div
                      className={`mt-2 rounded-full h-2 ${
                        refreshStatus.includes('failed') ||
                        refreshStatus.includes('Error')
                          ? 'bg-red-200'
                          : 'bg-blue-200'
                      }`}
                    >
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          refreshStatus.includes('failed') ||
                          refreshStatus.includes('Error')
                            ? 'bg-red-600'
                            : 'bg-blue-600'
                        }`}
                        style={{ width: `${refreshProgress}%` }}
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}

        {/* Scrollable content area */}
        <div
          ref={printRef}
          className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 sm:space-y-8 print:p-4 print:space-y-4"
          onScroll={handleModalScroll}
        >
          {/* Print Header - Only visible when printing */}
          <div className="hidden print:block print:mb-6 print:pb-4 print:border-b print:border-gray-300">
            <div className="flex justify-between items-center print:mb-2">
              <h1 className="text-2xl font-bold print:text-xl">
                Briefed Case Report
              </h1>
              <div className="text-sm text-gray-600 print:text-xs">
                Generated: {new Date().toLocaleDateString()}
              </div>
            </div>
            <div className="text-sm font-bold print:text-xs">
              Case #{caseData.caseNumber}
            </div>
          </div>

          <section className="overflow-x-auto print:overflow-visible">
            <SectionHeader icon={FileText} title="Primary Information" />
            <PrimaryInfo
              caseData={caseData}
              isCopyingCaseNumber={isCopyingCaseNumber}
              setIsCopyingCaseNumber={setIsCopyingCaseNumber}
            />
          </section>

          <section className="overflow-x-auto print:overflow-visible">
            <SectionHeader icon={Scale} title="Status & Sentence" />
            <StatusAndSentence caseData={caseData} />
          </section>

          <section className="overflow-x-auto print:overflow-visible">
            <SectionHeader icon={Clock} title="Future Proceedings" />
            <FutureProceedings caseData={caseData} />
          </section>

          <section className="overflow-x-auto print:overflow-visible">
            <SectionHeader icon={Users} title="Case Parties" />
            <CaseParties
              caseData={caseData}
              isEditingDefendants={isEditingDefendants}
              editedDefendants={editedDefendants}
              onDefendantChange={(index, field, value) => {
                const updated = [...editedDefendants];
                updated[index] = { ...updated[index], [field]: value };
                setEditedDefendants(updated);
              }}
              onSaveEdits={handleSaveEdits}
              onCancelEditing={handleCancelEditing}
              isSavingEdits={isSavingEdits}
              onStartEditing={() => setIsEditingDefendants(true)}
            />
          </section>

          <section className="overflow-x-auto print:overflow-visible">
            <SectionHeader icon={List} title="Counts" />
            <CountsTable counts={caseData.counts} />
          </section>
          <section className="overflow-x-auto print:hidden">
            <SectionHeader icon={MessageSquare} title="Comments" />
            <CommentsSection comments={caseData.comments} />
          </section>

          {/* Print Footer - Only visible when printing */}
          <div className="hidden print:block print:mt-8 print:pt-4 print:border-t print:border-gray-300 print:text-center print:text-xs print:text-gray-500">
            End of Report | Briefed | Generated{' '}
            {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  );
};

CaseDetailsModal.propTypes = {
  caseData: PropTypes.shape({
    caseNumber: PropTypes.string,
    isDismissed: PropTypes.bool,
    counts: PropTypes.arrayOf(CountPropType),
    comments: PropTypes.arrayOf(
      PropTypes.shape({
        userName: PropTypes.string,
        userFunction: PropTypes.string,
        date: PropTypes.string,
        text: PropTypes.string
      })
    )
  }),
  onClose: PropTypes.func.isRequired,
  onFindSimilar: PropTypes.func,
  onCaseUpdated: PropTypes.func
};

const PrimaryInfo = ({
  caseData,
  isCopyingCaseNumber,
  setIsCopyingCaseNumber
}) => {
  // No longer needed - potential judges are now included in the docket data

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 print:grid-cols-2 print:gap-3">
      <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
        <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
          Case Number
        </h3>
        <p className="mt-1 text-lg font-medium print:text-sm">
          {caseData.caseNumber}
        </p>
        <div className="mt-2 print:hidden">
          <button
            onClick={async () => {
              if (isCopyingCaseNumber) return; // Prevent multiple clicks

              setIsCopyingCaseNumber(true);
              try {
                await openLACourtWithCaseNumber(caseData.caseNumber);
              } catch (error) {
                console.error(
                  'Failed to open LA Court with case number:',
                  error
                );
              } finally {
                setIsCopyingCaseNumber(false);
              }
            }}
            disabled={isCopyingCaseNumber}
            className={`inline-flex items-center text-sm hover:underline ${
              isCopyingCaseNumber
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-600 hover:text-blue-800'
            }`}
            title={
              isCopyingCaseNumber
                ? 'Copying case number...'
                : 'Opens LA Court website and copies case number to clipboard - paste it into the search field'
            }
          >
            <svg
              className="w-3 h-3 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            {isCopyingCaseNumber
              ? 'Copying...'
              : 'Open LA Court Website & Copy Case Number'}
            <svg
              className="ml-1 w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </button>
          <p className="mt-1 text-xs text-gray-500">
            Case number will be copied to clipboard - paste it into the search
            field on the LA Court website
          </p>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
        <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
          Jurisdiction
        </h3>
        <p className="mt-1 text-lg font-medium print:text-sm">
          {caseData.jurisdiction}
        </p>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
        <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
          Courthouse
        </h3>
        <p className="mt-1 text-lg font-medium print:text-sm">
          {caseData.courthouse}
        </p>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
        <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
          Departments & Judges
        </h3>
        <div className="mt-1 space-y-3 print:space-y-2">
          {/* Departments */}
          {caseData.departments && caseData.departments.length > 0 ? (
            <div className="flex flex-wrap gap-1 print:gap-0.5">
              {caseData.departments.map((dept, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 font-medium print:bg-gray-100 print:text-gray-800 print:border print:border-gray-300 print:px-1 print:py-0.5"
                >
                  Dept {dept}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm print:text-xs">
              No departments assigned
            </p>
          )}

          {/* Judges */}
          <div className="border-t border-gray-200 pt-2 print:border-gray-300 print:pt-1">
            <div className="flex items-center gap-2 mb-1 print:mb-0.5">
              <Gavel className="w-3 h-3 text-gray-500 print:hidden" />
              <span className="text-xs font-medium text-gray-500 print:text-xs print:font-bold">
                Judges
              </span>
            </div>
            {(() => {
              // Filter out department-based judges (firstName = "Department")
              const validJudges =
                caseData.judges?.filter(
                  (judge) => judge.firstName && judge.firstName !== 'Department'
                ) || [];

              if (validJudges.length > 0) {
                // Show all judges (assigned and potential)
                return validJudges.map((judge, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900 print:text-xs">
                        {judge.firstName} {judge.lastName}
                      </p>
                      {judge.isPotential && (
                        <p className="text-xs text-gray-500 print:text-xs">
                          (Based on department)
                        </p>
                      )}
                    </div>
                    {judge.robingRoomUrl && (
                      <a
                        href={judge.robingRoomUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-xs underline print:hidden"
                        title="View judge profile on The Robing Room"
                      >
                        Robing Room
                      </a>
                    )}
                  </div>
                ));
              } else {
                return (
                  <p className="text-xs text-gray-400 print:text-xs">
                    No judges assigned
                  </p>
                );
              }
            })()}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
        <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
          Case Timeline
        </h3>
        <div className="mt-1">
          <p className="text-lg font-medium print:text-sm">
            {formatDateForDisplay(caseData.openDate)}
            {caseData.closeDate && (
              <>
                <span className="mx-2 text-gray-400 print:mx-1">→</span>
                {formatDateForDisplay(caseData.closeDate)}
              </>
            )}
          </p>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
        <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
          Duration
        </h3>
        <div className="mt-1 flex items-center gap-2 print:gap-1">
          <Clock className="w-4 h-4 text-gray-600 print:hidden" />
          <p className="text-lg font-medium print:text-sm">
            {caseData.durationDays ? `${caseData.durationDays} days` : 'N/A'}
          </p>
        </div>
      </div>
    </div>
  );
};

PrimaryInfo.propTypes = {
  caseData: PropTypes.shape({
    caseNumber: PropTypes.string.isRequired,
    jurisdiction: PropTypes.string.isRequired,
    courthouse: PropTypes.string.isRequired,
    departments: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ),
    judges: PropTypes.arrayOf(PersonPropType),
    openDate: PropTypes.string.isRequired,
    closeDate: PropTypes.string,
    durationDays: PropTypes.number
  }).isRequired,
  isCopyingCaseNumber: PropTypes.bool.isRequired,
  setIsCopyingCaseNumber: PropTypes.func.isRequired
};

const StatusAndSentence = ({ caseData }) => {
  const getSentencingTypes = () => {
    const types = [];
    if (
      caseData.totalConfinementMonths &&
      caseData.totalConfinementMonths > 0
    ) {
      types.push('Confinement');
    }
    if (caseData.totalConfinementDays && caseData.totalConfinementDays > 0) {
      types.push('Confinement');
    }
    if (caseData.totalFines && caseData.totalFines > 0) {
      types.push('Fine');
    }
    if (caseData.totalProbationMonths && caseData.totalProbationMonths > 0) {
      types.push('Probation');
    }
    if (caseData.hasDiversion) {
      types.push('Diversion');
    }
    if (caseData.isDismissed) {
      types.push('Dismissed');
    }
    return types;
  };

  return (
    <div className="grid grid-cols-2 gap-6 print:gap-3">
      <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
        <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
          Status
        </h3>
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize mt-1 border print:bg-gray-100 print:text-gray-800 print:border-gray-300 print:px-2 print:py-0.5 ${getStatusColorNoBorder(caseData.caseStatus)}`}
        >
          {caseData.caseStatus}
        </span>
      </div>
      <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
        <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
          Sentencing Summary
        </h3>
        <div className="mt-1 space-y-2 print:space-y-1">
          <p className="text-lg font-medium print:text-sm">
            {getSentencingSummary(caseData)}
          </p>
          {getSentencingTypes().length > 0 && (
            <div className="flex flex-wrap gap-1 print:gap-0.5">
              {getSentencingTypes().map((type, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium print:bg-gray-100 print:text-gray-800 print:border print:border-gray-300 print:px-1 print:py-0.5 ${
                    type === 'Confinement'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : type === 'Probation'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : type === 'Dismissed'
                          ? 'bg-gray-50 text-gray-700 border border-gray-200'
                          : 'bg-green-50 text-green-700 border border-green-200'
                  }`}
                >
                  {type}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

StatusAndSentence.propTypes = {
  caseData: PropTypes.shape({
    caseStatus: PropTypes.string.isRequired,
    totalConfinementMonths: PropTypes.number,
    totalConfinementDays: PropTypes.number,
    totalProbationMonths: PropTypes.number,
    totalFines: PropTypes.number,
    hasDiversion: PropTypes.bool,
    isDismissed: PropTypes.bool,
    sentencingTypes: PropTypes.arrayOf(PropTypes.string),
    counts: PropTypes.arrayOf(CountPropType)
  }).isRequired
};

const FutureProceedings = ({ caseData }) => {
  if (!caseData.futureProceedings || caseData.futureProceedings.length === 0) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
        <div className="mb-4 print:mb-2">
          <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
            Future Proceedings
          </h3>
          <p className="text-xs text-amber-600 mt-1 print:text-xs">
            ⚠️ Dates may contain errors - verify with official court documents
          </p>
        </div>
        <p className="text-gray-500 text-sm">
          No future proceedings scheduled.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
      <div className="mb-4 print:mb-2">
        <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
          Future Proceedings
        </h3>
        <p className="text-xs text-amber-600 mt-1 print:text-xs">
          ⚠️ May contain errors - verify with official court documents
        </p>
      </div>
      <div className="space-y-3">
        {caseData.futureProceedings.map((proceeding, index) => (
          <div
            key={index}
            className="bg-white p-3 rounded border print:border-gray-300"
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 text-sm print:text-xs">
                  {proceeding.type}
                </h4>
                <p className="text-gray-600 text-sm print:text-xs">
                  {proceeding.location} - {proceeding.department}
                </p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900 text-sm print:text-xs">
                  {proceeding.date}
                </p>
                <p className="text-gray-600 text-sm print:text-xs">
                  {proceeding.time}
                </p>
              </div>
            </div>
            {proceeding.result && (
              <p className="text-gray-500 text-xs print:text-xs">
                Result: {proceeding.result}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const CaseParties = ({
  caseData,
  isEditingDefendants,
  editedDefendants,
  onDefendantChange,
  onSaveEdits,
  onCancelEditing,
  isSavingEdits,
  onStartEditing
}) => (
  <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border print:border-gray-300 print:p-3">
    <div className="flex items-center justify-between mb-4 print:mb-2">
      <h3 className="text-sm font-medium text-gray-500 print:text-xs print:font-bold">
        Case Parties
      </h3>
      {!isEditingDefendants && onStartEditing && (
        <button
          onClick={onStartEditing}
          className="print:hidden flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
          type="button"
        >
          <Edit2 className="w-3 h-3" />
          Edit
        </button>
      )}
    </div>
    <div className="grid grid-cols-2 gap-x-8 gap-y-4 print:gap-x-4 print:gap-y-2">
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-500 print:text-xs print:font-bold">
          <User className="w-4 h-4 print:hidden" />
          <span>Defendants</span>
        </div>
        {(isEditingDefendants ? editedDefendants : caseData.defendants)?.map(
          (defendant, index) => (
            <div key={index} className="mt-1 pl-6 print:pl-4 print:mt-0.5">
              <p className="font-medium print:text-xs">
                {defendant.firstName} {defendant.lastName}
              </p>
              <div className="text-sm text-gray-500 mt-1 print:text-xs print:mt-0.5 space-y-0.5">
                {defendant.gender && <div>Gender: {defendant.gender}</div>}
                {isEditingDefendants ? (
                  <>
                    <div className="flex items-center gap-2">
                      <label className="text-xs">Date of Birth:</label>
                      <div className="relative flex items-center">
                        <input
                          type="date"
                          value={defendant.dateOfBirth || ''}
                          onChange={(e) =>
                            onDefendantChange(
                              index,
                              'dateOfBirth',
                              e.target.value || null
                            )
                          }
                          className="px-2 py-0.5 border border-gray-300 rounded text-xs pr-6"
                        />
                        {defendant.dateOfBirth && (
                          <button
                            type="button"
                            onClick={() =>
                              onDefendantChange(index, 'dateOfBirth', null)
                            }
                            className="absolute right-1 text-gray-400 hover:text-gray-600 p-0.5"
                            title="Clear date of birth"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs">Race:</label>
                      <select
                        value={defendant.race || ''}
                        onChange={(e) =>
                          onDefendantChange(
                            index,
                            'race',
                            e.target.value || null
                          )
                        }
                        className="px-2 py-0.5 border border-gray-300 rounded text-xs"
                      >
                        <option value="">N/A</option>
                        {RACES.map((race) => (
                          <option key={race} value={race}>
                            {race}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs">Gender:</label>
                      <select
                        value={defendant.gender || ''}
                        onChange={(e) =>
                          onDefendantChange(
                            index,
                            'gender',
                            e.target.value || null
                          )
                        }
                        className="px-2 py-0.5 border border-gray-300 rounded text-xs"
                      >
                        <option value="">N/A</option>
                        {GENDERS.map((gender) => (
                          <option key={gender} value={gender}>
                            {gender}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      Date of Birth:{' '}
                      {defendant.dateOfBirth
                        ? formatDateOnly(defendant.dateOfBirth) || 'N/A'
                        : 'N/A'}
                    </div>
                    <div>Race: {defendant.race || 'N/A'}</div>
                    <div>Gender: {defendant.gender || 'N/A'}</div>
                  </>
                )}
              </div>
            </div>
          )
        )}
        {isEditingDefendants && (
          <div className="flex items-center gap-2 mt-3 pl-6 print:hidden">
            <button
              onClick={onSaveEdits}
              disabled={isSavingEdits}
              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isSavingEdits ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={onCancelEditing}
              disabled={isSavingEdits}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 text-sm text-gray-500 print:text-xs print:font-bold">
          <Briefcase className="w-4 h-4 print:hidden" />
          <span>Prosecuting Attorneys</span>
        </div>
        {caseData.prosecutingAttorneys?.map((attorney, index) => (
          <p
            key={index}
            className="mt-1 font-medium pl-6 print:pl-4 print:text-xs print:mt-0.5"
          >
            {attorney.firstName} {attorney.lastName}
          </p>
        ))}
      </div>

      <div>
        <div className="flex items-center gap-2 text-sm text-gray-500 print:text-xs print:font-bold">
          <Briefcase className="w-4 h-4 print:hidden" />
          <span>Defense Attorneys</span>
        </div>
        {caseData.defenseAttorneys?.map((attorney, index) => (
          <p
            key={index}
            className="mt-1 font-medium pl-6 print:pl-4 print:text-xs print:mt-0.5"
          >
            {attorney.firstName} {attorney.lastName}
          </p>
        ))}
      </div>
    </div>
  </div>
);

FutureProceedings.propTypes = {
  caseData: PropTypes.shape({
    futureProceedings: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        time: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        result: PropTypes.string.isRequired,
        location: PropTypes.string.isRequired,
        department: PropTypes.string.isRequired
      })
    )
  }).isRequired
};

CaseParties.propTypes = {
  caseData: PropTypes.shape({
    defendants: PropTypes.arrayOf(PersonPropType),
    prosecutingAttorneys: PropTypes.arrayOf(PersonPropType),
    defenseAttorneys: PropTypes.arrayOf(PersonPropType)
  }).isRequired,
  isEditingDefendants: PropTypes.bool,
  editedDefendants: PropTypes.arrayOf(PersonPropType),
  onDefendantChange: PropTypes.func,
  onSaveEdits: PropTypes.func,
  onCancelEditing: PropTypes.func,
  isSavingEdits: PropTypes.bool,
  onStartEditing: PropTypes.func
};

const Charges = ({ charges }) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <h3 className="text-sm font-medium text-gray-500 mb-4">Offenses</h3>
    <div className="flex flex-wrap gap-2">
      {charges.map((charge, index) => (
        <span
          key={index}
          className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm"
        >
          {charge}
        </span>
      ))}
    </div>
  </div>
);

Charges.propTypes = {
  charges: PropTypes.arrayOf(PropTypes.string).isRequired
};

const CountsTable = ({ counts }) => {
  const getSentencingTypes = (count) => {
    if (!count.sentencing) return [];

    const types = [];
    if (
      count.sentencing.confinements &&
      count.sentencing.confinements.length > 0
    ) {
      // Check if any confinements are fines
      const hasFines = count.sentencing.confinements.some((c) => c.fine);
      const hasTimeSentences = count.sentencing.confinements.some(
        (c) => c.sentence
      );
      const hasDismissals = count.sentencing.confinements.some(
        (c) => c.dismissal
      );
      const hasFugitiveExtraditions = count.sentencing.confinements.some(
        (c) => c.fugitiveExtradition
      );

      if (hasFines) {
        types.push('Fine');
      }
      if (hasTimeSentences) {
        types.push('Confinement');
      }
      if (hasDismissals) {
        types.push('Dismissed');
      }
      if (hasFugitiveExtraditions) {
        types.push('Fugitive Extradition');
      }
    }
    if (count.sentencing.probations && count.sentencing.probations.length > 0) {
      if (count.sentencing.probations.some((p) => p.isDiversion)) {
        types.push('Diversion');
      } else {
        types.push('Probation');
      }
    }
    return types;
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto print:bg-white print:border print:border-gray-300 print:p-3 print:overflow-visible">
      <h3 className="text-sm font-medium text-gray-500 mb-4 print:text-xs print:font-bold print:mb-2">
        Counts & Sentencing
      </h3>
      <table className="min-w-full divide-y divide-gray-200 print:divide-gray-300">
        <thead className="bg-gray-100 print:bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-2 print:py-1 print:text-xs">
              Count
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-2 print:py-1 print:text-xs">
              Code
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-2 print:py-1 print:text-xs">
              Description
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-2 print:py-1 print:text-xs">
              Sentencing
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-2 print:py-1 print:text-xs">
              Types
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 print:divide-gray-300">
          {counts.map((count, index) => {
            const sentencingTypes = getSentencingTypes(count);
            return (
              <tr key={index}>
                <td className="px-4 py-2 text-sm font-medium print:px-2 print:py-1 print:text-xs">
                  {count.countNumber}
                </td>
                <td className="px-4 py-2 text-sm font-mono print:px-2 print:py-1 print:text-xs">
                  {count.chargeCode}
                </td>
                <td className="px-4 py-2 text-sm print:px-2 print:py-1 print:text-xs">
                  {count.chargeDescription}
                </td>
                <td className="px-4 py-2 text-sm print:px-2 print:py-1 print:text-xs">
                  <div className="space-y-1 print:space-y-0.5">
                    <div>{getSentencingDisplay(count)}</div>
                    {count.sentencing?.comments &&
                      count.sentencing.comments.length > 0 && (
                        <div className="text-xs text-gray-500 print:text-xs">
                          {count.sentencing.comments.map((comment, i) => (
                            <div key={i} className="italic">
                              &ldquo;{comment}&rdquo;
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </td>
                <td className="px-4 py-2 text-sm print:px-2 print:py-1 print:text-xs">
                  {sentencingTypes.length > 0 && (
                    <div className="flex flex-wrap gap-1 print:gap-0.5">
                      {sentencingTypes.map((type, i) => (
                        <span
                          key={i}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium print:bg-gray-100 print:text-gray-800 print:border print:border-gray-300 print:px-1 print:py-0.5 ${
                            type === 'Confinement'
                              ? 'bg-red-50 text-red-700 border border-red-200'
                              : type === 'Probation'
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : type === 'Fine'
                                  ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                  : type === 'Dismissed'
                                    ? 'bg-gray-50 text-gray-700 border border-gray-200'
                                    : type === 'Fugitive Extradition'
                                      ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                      : 'bg-green-50 text-green-700 border border-green-200'
                          }`}
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

CountsTable.propTypes = {
  counts: PropTypes.arrayOf(CountPropType).isRequired
};

export default CaseDetailsModal;
